// T-127 A (v1.8.42, Session 39, 30.09.2026): "Jetzt bezahlen" im Kundenkonto und Zahlbetrag im Checkout aus der zahlbaren Rechnung.
// Die Antwort von api/payment/status/{id} enthält seit v1.8.42 zahlbareRechnung, onlineZahlungMoeglich und keineZahlungOffen
// (Server-Regel Business.Zahlung.JetztBezahlen). Reine Funktionen, getestet in jetztBezahlen.test.ts.

export interface ZahlbareRechnung {
  invoiceId: number;
  invoiceNumber: string;
  type: string;
  bezeichnung: string;
  amountDue: number;
  dueDate?: string | null;
}

export interface ZahlStatus {
  zahlbareRechnung: ZahlbareRechnung | null;
  onlineZahlungMoeglich: boolean;
  keineZahlungOffen: boolean;
  /** Review v1.8.42 Befund 1: PayPal prüft eine Zahlung der Buchung (kein Zahlformular, kein Knopf) */
  zahlungInPruefung: boolean;
  /** Review v1.8.42 Befund 5: Buchungsstatus sperrt die Zahlung (storniert, offenes Angebot) */
  zahlungGesperrt: boolean;
  /** T-151 (v1.8.48): Knopf "Schlussrechnung jetzt anfordern" (Server-Regel Business.Zahlung.SchlussrechnungAnfordern) */
  schlussrechnungAnfordernMoeglich: boolean;
  /** T-188 (v1.8.60): Anforderung ergibt eine Rechnung über den Gesamtbetrag (Anzahlung unbezahlt, wird storniert) */
  schlussrechnungAlsKomplettrechnung: boolean;
}

const LEER: ZahlStatus = { zahlbareRechnung: null, onlineZahlungMoeglich: false, keineZahlungOffen: false, zahlungInPruefung: false, zahlungGesperrt: false, schlussrechnungAnfordernMoeglich: false, schlussrechnungAlsKomplettrechnung: false };

/** Liest die Felder aus der Status-Antwort (camelCase oder PascalCase); fehlende oder ungültige Werte ergeben "nichts". */
export function zahlStatusAus(daten: unknown): ZahlStatus {
  if (!daten || typeof daten !== 'object') return LEER;
  const d = daten as Record<string, unknown>;
  const roh = (d.zahlbareRechnung ?? d.ZahlbareRechnung) as Record<string, unknown> | null | undefined;
  let rechnung: ZahlbareRechnung | null = null;
  if (roh && typeof roh === 'object') {
    const betrag = Number(roh.amountDue ?? roh.AmountDue);
    const nummer = String(roh.invoiceNumber ?? roh.InvoiceNumber ?? '');
    if (isFinite(betrag) && betrag > 0 && nummer) {
      rechnung = {
        invoiceId: Number(roh.invoiceId ?? roh.InvoiceId ?? 0),
        invoiceNumber: nummer,
        type: String(roh.type ?? roh.Type ?? ''),
        bezeichnung: String(roh.bezeichnung ?? roh.Bezeichnung ?? 'Rechnung'),
        amountDue: Math.round(betrag * 100) / 100,
        dueDate: (roh.dueDate ?? roh.DueDate ?? null) as string | null,
      };
    }
  }
  const moeglich = (d.onlineZahlungMoeglich ?? d.OnlineZahlungMoeglich) === true;
  const nichtsOffen = (d.keineZahlungOffen ?? d.KeineZahlungOffen) === true;
  const inPruefung = (d.zahlungInPruefung ?? d.ZahlungInPruefung) === true;
  const gesperrt = (d.zahlungGesperrt ?? d.ZahlungGesperrt) === true;
  const anfordern = (d.schlussrechnungAnfordernMoeglich ?? d.SchlussrechnungAnfordernMoeglich) === true;
  const komplett = (d.schlussrechnungAlsKomplettrechnung ?? d.SchlussrechnungAlsKomplettrechnung) === true;
  return {
    zahlbareRechnung: rechnung,
    onlineZahlungMoeglich: moeglich && rechnung !== null && !inPruefung && !gesperrt,
    keineZahlungOffen: nichtsOffen && rechnung === null,
    zahlungInPruefung: inPruefung,
    zahlungGesperrt: gesperrt,
    // T-151: nicht während einer PayPal-Prüfung und nicht bei gesperrtem Status
    schlussrechnungAnfordernMoeglich: anfordern && !inPruefung && !gesperrt,
    schlussrechnungAlsKomplettrechnung: anfordern && komplett && !inPruefung && !gesperrt,
  };
}

/** T-151 (v1.8.48): Erklärung unter dem Knopf "Schlussrechnung jetzt anfordern" (AGB 02.10.2026). */
export const SCHLUSSRECHNUNG_ANFORDERN_TEXT =
  'Die Schlussrechnung erhalten Sie automatisch 25 Tage vor der Fahrt. Sie können sie auch jetzt anfordern und den Restbetrag gleich bezahlen; sie ist dann innerhalb von 7 Tagen zahlbar. Spätere Nachmeldungen rechnen wir mit einer Nachtragsrechnung ab.';

/** T-188 (v1.8.60): Erklärung, wenn auf die Anzahlung noch nichts bezahlt ist (Rechnung über den Gesamtbetrag statt Schlussrechnung über den Rest). */
export const SCHLUSSRECHNUNG_ANFORDERN_TEXT_KOMPLETT =
  'Die Schlussrechnung erhalten Sie automatisch 25 Tage vor der Fahrt. Da auf Ihre Anzahlung noch nichts bezahlt ist, erhalten Sie bei Anforderung jetzt eine Rechnung über den Gesamtbetrag, die sofort fällig ist; die Anzahlungsrechnung wird dabei aufgehoben. Spätere Nachmeldungen rechnen wir mit einer Nachtragsrechnung ab.';

/** T-188: passender Text unter dem Knopf "Schlussrechnung jetzt anfordern". */
export function schlussrechnungAnfordernText(komplett: boolean): string {
  return komplett ? SCHLUSSRECHNUNG_ANFORDERN_TEXT_KOMPLETT : SCHLUSSRECHNUNG_ANFORDERN_TEXT;
}

const euro = (v: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v);

/** Fälligkeit als TT.MM.JJJJ aus einem ISO-Datum, sonst leer. */
export function faelligText(iso?: string | null): string {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null;
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
}

/** Zeile für das Kundenkonto, z. B. "Offen: Anzahlung 11,80 € (Rechnung AN-2026-09-0008, fällig 13.10.2026)". */
export function offenText(r: ZahlbareRechnung | null): string {
  if (!r) return '';
  const f = faelligText(r.dueDate);
  return `Offen: ${r.bezeichnung} ${euro(r.amountDue)} (Rechnung ${r.invoiceNumber}${f ? `, fällig ${f}` : ''})`;
}

/** Betrag in Cent für den Checkout, sonst null (dann rechnet der Checkout wie bisher). */
export function zahlbetragCent(r: ZahlbareRechnung | null): number | null {
  return r ? Math.round(r.amountDue * 100) : null;
}
